import React, {
  createContext, useContext, useState, useRef, useCallback, useEffect, useLayoutEffect, useMemo,
} from 'react';
import { db } from '../firebase';
import { collection, collectionGroup, getDocs, query, where, orderBy, limit, Timestamp } from 'firebase/firestore';

// ═══════════════════════════════════════════════════════════════════════════════
// Sesión 32 · Entrega B · Datos compartidos entre módulos + estado de listados persistente
//
// QUÉ HACE
//  1. Almacén de datos en memoria (leads, oportunidades, presupuestos, clientes, proyectos).
//     - Carga perezosa POR COLECCIÓN: solo se descarga lo que un módulo pide, la primera vez.
//     - Vigencia de 30 minutos: pasado ese tiempo, al entrar en un módulo se recarga esa colección
//       (los datos antiguos se siguen viendo mientras tanto). Botón "Actualizar" en cada listado.
//     - Los módulos escriben en Firestore como siempre y además parchean la copia en memoria.
//  2. Estado de listados (búsqueda, filtros, orden, filas visibles, scroll) que sobrevive a abrir
//     una ficha y a cambiar de módulo dentro de la misma sesión. Nunca entre recargas del navegador.
//
// REGLAS
//  - El almacén guarda documentos CRUDOS. Nombres de lead/cliente y cálculos se derivan con useMemo
//    en cada módulo: así renombrar un lead se refleja en todas partes.
//  - Cerrar sesión desmonta Layout y vacía el almacén.
//  - Contador de lecturas: window.__metronLecturas (consola del navegador) para medir el consumo.
// ═══════════════════════════════════════════════════════════════════════════════

const TTL_MS = 30 * 60 * 1000;

export const toTs = (f) => {
  if (!f) return 0;
  if (f?.seconds) return f.seconds;
  if (f instanceof Date) return f.getTime() / 1000;
  if (typeof f === 'string') return new Date(f).getTime() / 1000;
  if (f?.toDate) return f.toDate().getTime() / 1000;
  return 0;
};

/**
 * En memoria las fechas se guardan como Timestamp, igual que vienen de Firestore (los filtros por año y las
 * ordenaciones dependen de ello). Convierte los valores Date de un objeto antes de ponerlos en el almacén.
 */
export const fechasATimestamp = (obj) => {
  const r = { ...obj };
  Object.keys(r).forEach(k => { if (r[k] instanceof Date) r[k] = Timestamp.fromDate(r[k]); });
  return r;
};

const ordenarDesc = (arr, campo) => [...arr].sort((a, b) => toTs(b[campo]) - toTs(a[campo]));

// Cada cargador devuelve documentos crudos. Se ordena en memoria (no con orderBy) para que un
// documento sin ese campo no quede fuera de la colección.
const CARGADORES = {
  leads: async () => {
    const snap = await getDocs(collection(db, 'leads'));
    return ordenarDesc(snap.docs.map(d => ({ id: d.id, ...d.data() })), 'fecha_alta');
  },
  oportunidades: async () => {
    // Subcolecciones posibles_proyectos de todos los leads, en una sola consulta de grupo.
    const snap = await getDocs(collectionGroup(db, 'posibles_proyectos'));
    return snap.docs.map(d => ({ id: d.id, leadId: d.ref.parent.parent.id, ...d.data() }));
  },
  presupuestos: async () => {
    const snap = await getDocs(collection(db, 'presupuestos'));
    return ordenarDesc(snap.docs.map(d => ({ id: d.id, ...d.data() })), 'fecha_presupuesto');
  },
  clientes: async () => {
    const snap = await getDocs(collection(db, 'clientes'));
    return ordenarDesc(snap.docs.map(d => ({ id: d.id, ...d.data() })), 'creado_en');
  },
  proyectos: async () => {
    const snap = await getDocs(collection(db, 'proyectos'));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  },
};

const NOMBRES = Object.keys(CARGADORES);
const VACIO = { items: [], estado: 'vacio', cargadoEn: 0, error: null };
const INICIAL = NOMBRES.reduce((acc, n) => ({ ...acc, [n]: VACIO }), {});
const CARGADO = (estado) => estado === 'listo' || estado === 'actualizando';

const ApiCtx = createContext(null);
const DatosCtx = createContext(INICIAL);
const HorasCtx = createContext({ version: 0, gen: 0 });

// Almacén de horas para Análisis (sesión 33). docs: id → registro; anios / proyectos: trozos ya leídos.
const horasVacio = (gen) => ({ docs: new Map(), anios: new Set(), proyectos: new Set(), pendientes: new Map(), cargadoEn: 0, gen });

export function DatosProvider({ refMain, children }) {
  const almacenRef = useRef(INICIAL);          // verdad síncrona
  const [datos, setDatos] = useState(INICIAL); // lo que ven los componentes
  const pendientes = useRef({});               // cargas en curso (evita duplicarlas)
  const versiones = useRef({});                // se incrementa con cada parche en memoria
  const uiRef = useRef({});                    // estado de listados (filtros, scroll) y borradores
  // Sesión 34: si hay borradores sin guardar, el navegador pregunta antes de cerrar o recargar la pestaña
  useEffect(() => {
    const aviso = (e) => {
      if (Object.keys(uiRef.current).some(k => k.startsWith('borrador.'))) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', aviso);
    return () => window.removeEventListener('beforeunload', aviso);
  }, []);
  const lecturas = useRef({});

  const contarLecturas = useCallback((nombre, n) => {
    lecturas.current[nombre] = (lecturas.current[nombre] || 0) + n;
    const total = Object.values(lecturas.current).reduce((s, x) => s + x, 0);
    window.__metronLecturas = { total, porColeccion: { ...lecturas.current } };
    if (process.env.NODE_ENV !== 'production') {
      console.info(`[METRON lecturas] ${nombre}: ${n} · acumuladas en esta sesión: ${total}`);
    }
  }, []);

  const escribir = useCallback((nombre, cambios) => {
    const nuevo = { ...almacenRef.current, [nombre]: { ...almacenRef.current[nombre], ...cambios } };
    almacenRef.current = nuevo;
    setDatos(nuevo);
  }, []);

  const cargar = useCallback((nombre, { forzar = false } = {}) => {
    if (!CARGADORES[nombre]) return Promise.resolve([]);
    if (pendientes.current[nombre]) return pendientes.current[nombre];
    const actual = almacenRef.current[nombre];
    const vigente = actual.estado === 'listo' && Date.now() - actual.cargadoEn <= TTL_MS;
    if (!forzar && vigente) return Promise.resolve(actual.items);

    const promesa = (async () => {
      const teniaDatos = CARGADO(actual.estado);
      escribir(nombre, { estado: teniaDatos ? 'actualizando' : 'cargando', error: null });
      const versionInicial = versiones.current[nombre] || 0;
      try {
        const items = await CARGADORES[nombre]();
        contarLecturas(nombre, items.length);
        if ((versiones.current[nombre] || 0) !== versionInicial) {
          // Se escribió algo mientras se descargaba: la copia en memoria es más reciente. Se conserva
          // y se deja sin renovar la vigencia para que la próxima entrada vuelva a intentarlo.
          escribir(nombre, { estado: 'listo', error: null });
        } else {
          escribir(nombre, { items, estado: 'listo', cargadoEn: Date.now(), error: null });
        }
      } catch (e) {
        console.error(`Error cargando ${nombre}:`, e);
        escribir(nombre, { estado: teniaDatos ? 'listo' : 'error', error: e.message || String(e) });
      } finally {
        delete pendientes.current[nombre];
      }
      return almacenRef.current[nombre].items;
    })();
    pendientes.current[nombre] = promesa;
    return promesa;
  }, [escribir, contarLecturas]);

  // Sustituye los items de una colección ya cargada (para los setters que usan los módulos).
  const reemplazar = useCallback((nombre, valor) => {
    const a = almacenRef.current[nombre];
    if (!a || !CARGADO(a.estado)) return;
    const items = typeof valor === 'function' ? valor(a.items) : valor;
    versiones.current[nombre] = (versiones.current[nombre] || 0) + 1;
    escribir(nombre, { items });
  }, [escribir]);

  // Igual que reemplazar, pero pensado para módulos que NO usan la colección: si no está cargada
  // no hace nada (la próxima carga ya traerá el dato actual de Firestore).
  const parchear = reemplazar;

  const obtener = useCallback(async (nombre) => {
    await cargar(nombre);
    return almacenRef.current[nombre].items;
  }, [cargar]);

  // ─── Horas para Análisis (sesión 33) ─────────────────────────────────────────
  // registros_horas NUNCA se descarga entera. Se lee por trozos: un año natural (por fecha) o todos los
  // registros de unos proyectos. Cada trozo se lee una sola vez por sesión; vigencia de 30 minutos.
  const horasRef = useRef(horasVacio(0));
  const versionHoras = useRef(0);
  const primerAnioRef = useRef(undefined);
  const [horasEstado, setHorasEstado] = useState({ version: 0, gen: 0 });

  const avisarHoras = useCallback(() => {
    versionHoras.current += 1;
    setHorasEstado({ version: versionHoras.current, gen: horasRef.current.gen });
  }, []);

  const asegurarHoras = useCallback(({ anios = [], proyectos = [] }) => {
    let h = horasRef.current;
    if (h.cargadoEn && Date.now() - h.cargadoEn > TTL_MS && h.pendientes.size === 0) {
      horasRef.current = horasVacio(h.gen + 1);
      h = horasRef.current;
      avisarHoras();
    }
    const gen = h.gen;
    const base = collection(db, 'registros_horas');
    const leer = async (consulta, marcar) => {
      const snap = await getDocs(consulta);
      contarLecturas('registros_horas', snap.size);
      if (horasRef.current.gen !== gen) return; // el almacén se reinició mientras se leía
      snap.docs.forEach(d => h.docs.set(d.id, { id: d.id, ...d.data() }));
      marcar();
      if (!h.cargadoEn) h.cargadoEn = Date.now();
      avisarHoras();
    };
    const tareas = [];
    [...new Set(anios.map(String))].forEach(a => {
      if (h.anios.has(a)) return;
      const clave = 'a' + a;
      if (!h.pendientes.has(clave)) {
        const p = leer(query(base, where('fecha', '>=', `${a}-01-01`), where('fecha', '<=', `${a}-12-31`)), () => h.anios.add(a))
          .finally(() => h.pendientes.delete(clave));
        h.pendientes.set(clave, p);
      }
      tareas.push(h.pendientes.get(clave));
    });
    const nuevos = [];
    [...new Set(proyectos.filter(Boolean))].forEach(id => {
      if (h.proyectos.has(id)) return;
      if (h.pendientes.has('p' + id)) tareas.push(h.pendientes.get('p' + id));
      else nuevos.push(id);
    });
    for (let i = 0; i < nuevos.length; i += 30) { // 'in' admite como máximo 30 valores
      const trozo = nuevos.slice(i, i + 30);
      const p = leer(query(base, where('proyectoId', 'in', trozo)), () => trozo.forEach(id => h.proyectos.add(id)))
        .finally(() => trozo.forEach(id => h.pendientes.delete('p' + id)));
      trozo.forEach(id => h.pendientes.set('p' + id, p));
      tareas.push(p);
    }
    return Promise.all(tareas);
  }, [avisarHoras, contarLecturas]);

  const horasListas = useCallback((anios = [], proyectos = []) => {
    const h = horasRef.current;
    return anios.every(a => h.anios.has(String(a))) && proyectos.every(id => !id || h.proyectos.has(id));
  }, []);

  // Primer año con horas (para la lista de años de Productividad): 1 lectura, una vez por sesión.
  const primerAnioHoras = useCallback(async () => {
    if (primerAnioRef.current !== undefined) return primerAnioRef.current;
    const snap = await getDocs(query(collection(db, 'registros_horas'), orderBy('fecha'), limit(1)));
    contarLecturas('registros_horas', snap.size);
    primerAnioRef.current = snap.empty ? null : String(snap.docs[0].data().fecha || '').slice(0, 4) || null;
    return primerAnioRef.current;
  }, [contarLecturas]);

  // Control de Horas avisa de un alta/edición (datos) o de un borrado (datos = null).
  // Solo se guarda en memoria si pertenece a un trozo ya leído; si no, la próxima lectura ya lo traerá.
  const parchearHoras = useCallback((id, datos) => {
    const h = horasRef.current;
    if (!h.cargadoEn) return;
    if (datos === null) { h.docs.delete(id); avisarHoras(); return; }
    const d = { ...(h.docs.get(id) || {}), ...datos, id };
    const anio = typeof d.fecha === 'string' ? d.fecha.slice(0, 4) : null;
    if ((anio && h.anios.has(anio)) || (d.proyectoId && h.proyectos.has(d.proyectoId))) h.docs.set(id, d);
    else h.docs.delete(id);
    avisarHoras();
  }, [avisarHoras]);

  const api = useMemo(() => ({
    cargar, reemplazar, parchear, obtener, uiRef, refMain,
    asegurarHoras, horasListas, horasRef, primerAnioHoras, parchearHoras,
  }), [cargar, reemplazar, parchear, obtener, refMain, asegurarHoras, horasListas, primerAnioHoras, parchearHoras]);

  return (
    <ApiCtx.Provider value={api}>
      <HorasCtx.Provider value={horasEstado}>
        <DatosCtx.Provider value={datos}>{children}</DatosCtx.Provider>
      </HorasCtx.Provider>
    </ApiCtx.Provider>
  );
}

// ─── Hooks de datos ───────────────────────────────────────────────────────────

/** Uso: const { items: leads, setItems: setLeads, cargando } = useColeccion('leads'); */
export function useColeccion(nombre) {
  const api = useContext(ApiCtx);
  const datos = useContext(DatosCtx);
  const col = datos[nombre] || VACIO;
  useEffect(() => { api.cargar(nombre); }, [api, nombre]);
  const setItems = useCallback((valor) => api.reemplazar(nombre, valor), [api, nombre]);
  const recargar = useCallback(() => api.cargar(nombre, { forzar: true }), [api, nombre]);
  return {
    items: col.items,
    setItems,
    recargar,
    cargando: col.estado === 'vacio' || col.estado === 'cargando',
    actualizando: col.estado === 'actualizando',
    error: col.error,
    cargadoEn: col.cargadoEn,
  };
}

/** Parchea en memoria una colección que este módulo no carga. Devuelve una función (fn) => void. */
export function useParchear(nombre) {
  const api = useContext(ApiCtx);
  return useCallback((fn) => api.parchear(nombre, fn), [api, nombre]);
}

/** Para código asíncrono que necesita los datos completos (p. ej. Análisis). */
export function useObtener() {
  return useContext(ApiCtx).obtener;
}

// ─── Horas (sesión 33) ────────────────────────────────────────────────────────

/**
 * Registros de horas para Análisis. Pide los trozos que necesita (años naturales y/o proyectos) y devuelve
 * todos los registros en memoria cuando esos trozos están leídos. Mientras se lee un trozo nuevo devuelve
 * la última foto completa (listo = false). primeraCarga = todavía no hay ninguna foto que enseñar.
 */
export function useHoras({ anios = [], proyectos = [] }) {
  const api = useContext(ApiCtx);
  const { version, gen } = useContext(HorasCtx);
  const clave = JSON.stringify([[...new Set(anios.map(String))].sort(), [...new Set(proyectos.filter(Boolean))].sort()]);
  const [error, setError] = useState(null);
  useEffect(() => {
    const [a, p] = JSON.parse(clave);
    let vivo = true;
    setError(null);
    api.asegurarHoras({ anios: a, proyectos: p }).catch(e => {
      console.error('Error cargando horas:', e);
      if (vivo) setError('No se pudieron cargar las horas. Revisa la consola del navegador (F12).');
    });
    return () => { vivo = false; };
  }, [api, clave, gen]);
  // version cambia cada vez que el almacén de horas cambia: fuerza a recalcular la foto.
  const listo = useMemo(() => { const [a, p] = JSON.parse(clave); return api.horasListas(a, p); }, [api, clave, version]); // eslint-disable-line react-hooks/exhaustive-deps
  const registros = useMemo(() => [...api.horasRef.current.docs.values()], [api, version]); // eslint-disable-line react-hooks/exhaustive-deps
  const ultimo = useRef(null);
  if (listo) ultimo.current = registros;
  return { registros: ultimo.current || [], listo, primeraCarga: ultimo.current === null, error };
}

/** Primer año con registros de horas (texto 'AAAA') o null mientras se lee. */
export function usePrimerAnioHoras() {
  const api = useContext(ApiCtx);
  const [anio, setAnio] = useState(null);
  useEffect(() => {
    let vivo = true;
    api.primerAnioHoras().then(a => { if (vivo) setAnio(a); }).catch(e => console.error('Error leyendo el primer año de horas:', e));
    return () => { vivo = false; };
  }, [api]);
  return anio;
}

/** Para Control de Horas: parchearHoras(id, datos) tras guardar; parchearHoras(id, null) tras borrar. */
export function useParchearHoras() {
  return useContext(ApiCtx).parchearHoras;
}

// ─── Barra "Actualizado a las HH:MM · Actualizar" ─────────────────────────────

export function BarraActualizacion({ colecciones }) {
  const api = useContext(ApiCtx);
  const datos = useContext(DatosCtx);
  const infos = colecciones.map(n => datos[n] || VACIO);
  const trabajando = infos.some(i => i.estado === 'cargando' || i.estado === 'actualizando');
  const cargadas = infos.filter(i => i.cargadoEn > 0);
  const masAntigua = cargadas.length ? Math.min(...cargadas.map(i => i.cargadoEn)) : 0;
  const registros = infos.reduce((s, i) => s + i.items.length, 0);
  const hora = masAntigua
    ? new Date(masAntigua).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
    : '';
  const hayError = infos.some(i => i.error);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <span style={{ color: hayError ? '#D85A30' : '#AFA9EC', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
        {trabajando ? 'Actualizando…' : hayError ? 'No se pudo actualizar' : hora ? `Actualizado a las ${hora}` : ''}
      </span>
      <button
        disabled={trabajando}
        title={`Vuelve a leer ${new Intl.NumberFormat('es-ES').format(registros)} registros del servidor (cuenta como ${new Intl.NumberFormat('es-ES').format(registros)} lecturas)`}
        onClick={() => colecciones.forEach(n => api.cargar(n, { forzar: true }))}
        style={{
          background: 'transparent', color: '#AFA9EC', border: '1px solid rgba(83,74,183,0.4)', borderRadius: '8px',
          padding: '7px 12px', fontSize: '0.8rem', cursor: trabajando ? 'default' : 'pointer', opacity: trabajando ? 0.5 : 1,
          fontFamily: 'DM Sans, sans-serif',
        }}
      >
        ↻ Actualizar
      </button>
    </div>
  );
}

// ─── Estado de listados persistente ───────────────────────────────────────────

/** Como useState, pero el valor se recuerda mientras dure la sesión aunque el componente se desmonte. */
export function useEstadoPersistente(clave, valorInicial) {
  const { uiRef } = useContext(ApiCtx);
  const [valor, setValor] = useState(() => (uiRef.current[clave] !== undefined ? uiRef.current[clave] : valorInicial));
  useEffect(() => { uiRef.current[clave] = valor; }, [uiRef, clave, valor]);
  return [valor, setValor];
}

/** Ejecuta fn solo cuando cambia alguna dependencia (nunca en el montaje). Seguro con StrictMode. */
export function useAlCambiar(fn, deps) {
  const previas = useRef(deps);
  useEffect(() => {
    const cambio = deps.length !== previas.current.length || deps.some((d, i) => !Object.is(d, previas.current[i]));
    previas.current = deps;
    if (cambio) fn();
  });
}

/**
 * Recuerda y restaura la posición de scroll del contenedor principal (<main> de Layout).
 * listo: false mientras no se pueda restaurar (datos cargando, o ficha abierta en lugar del listado).
 */
export function usePosicionScroll(clave, listo = true) {
  const { uiRef, refMain } = useContext(ApiCtx);
  const restaurado = useRef(false);
  useLayoutEffect(() => {
    const main = refMain && refMain.current;
    if (!main || !listo) return undefined;
    if (!restaurado.current) {
      restaurado.current = true;
      main.scrollTop = uiRef.current['scroll.' + clave] || 0;
    }
    const guardar = () => { uiRef.current['scroll.' + clave] = main.scrollTop; };
    main.addEventListener('scroll', guardar, { passive: true });
    return () => {
      main.removeEventListener('scroll', guardar);
      restaurado.current = false;
    };
  }, [uiRef, refMain, clave, listo]);
}

// ─── Borradores (sesión 34) ───────────────────────────────────────────────────
// Lo escrito en un formulario sin guardar se conserva en la memoria de la sesión al salir a otro módulo.
// clave: '<tipo>:<id>' o '<tipo>:nuevo' (p. ej. 'cliente:abc123', 'proyecto:nuevo'). Dura mientras no se recargue la
// página ni se cierre la sesión. Se borra al guardar o cancelar (descartar).
const igual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/** Como useState para el formulario, pero recuerda el borrador. clave null = sin borrador (useState normal).
 *  Devuelve [valor, setValor, recuperado, reiniciar]. Al volver al valor inicial, el borrador se borra solo. */
export function useBorrador(clave, inicial) {
  const { uiRef } = useContext(ApiCtx);
  const k = clave ? 'borrador.' + clave : null;
  const tipo = clave ? clave.split(':')[0] : null;
  const inicialRef = useRef(inicial);
  const [recuperado] = useState(() => !!k && uiRef.current[k] !== undefined);
  const [valor, setValor] = useState(() => (k && uiRef.current[k] !== undefined ? uiRef.current[k] : inicial));
  useEffect(() => {
    if (!k) return;
    if (igual(valor, inicialRef.current)) {
      delete uiRef.current[k];
      if (uiRef.current['borrador_ultimo.' + tipo] === clave) delete uiRef.current['borrador_ultimo.' + tipo];
    } else {
      uiRef.current[k] = valor;
      uiRef.current['borrador_ultimo.' + tipo] = clave;
    }
  }, [uiRef, k, tipo, clave, valor]);
  // reiniciar(nuevo): toma "nuevo" como punto de partida (tras guardar o al salir sin guardar) y borra el borrador
  const reiniciar = useCallback((nuevo) => { inicialRef.current = nuevo; setValor(nuevo); }, []);
  return [valor, setValor, recuperado, reiniciar];
}

/** Borra un borrador (al guardar o al confirmar "salir sin guardar"). Devuelve fn(clave). */
export function useDescartarBorrador() {
  const { uiRef } = useContext(ApiCtx);
  return useCallback((clave) => {
    if (!clave) return;
    delete uiRef.current['borrador.' + clave];
    const tipo = clave.split(':')[0];
    if (uiRef.current['borrador_ultimo.' + tipo] === clave) delete uiRef.current['borrador_ultimo.' + tipo];
  }, [uiRef]);
}

/** ¿Hay un borrador para esta clave? (sin suscribirse a cambios) */
export function useHayBorrador(clave) {
  const { uiRef } = useContext(ApiCtx);
  return uiRef.current['borrador.' + clave] !== undefined;
}

/** Clave del último borrador pendiente de un tipo ('cliente', 'proyecto'...), leída al montar el módulo. */
export function useBorradorPendiente(tipo) {
  const { uiRef } = useContext(ApiCtx);
  const [pendiente] = useState(() => {
    const clave = uiRef.current['borrador_ultimo.' + tipo];
    return clave && uiRef.current['borrador.' + clave] !== undefined ? clave.split(':').slice(1).join(':') : null;
  });
  return pendiente;
}

/** Aviso que se muestra en un formulario con borrador recuperado. */
export function AvisoBorrador({ onDescartar }) {
  return (
    <div style={{ margin: '0 0 16px', background: 'rgba(83,74,183,0.15)', border: '1px solid rgba(123,111,232,0.5)', borderRadius: '10px', padding: '10px 14px', display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
      <span style={{ color: '#AFA9EC', fontSize: '0.85rem' }}>Tienes cambios sin guardar de la última vez que editaste esta ficha.</span>
      <button onClick={onDescartar} style={{ background: 'transparent', color: '#D85A30', border: '1px solid rgba(216,90,48,0.6)', borderRadius: '8px', padding: '6px 12px', fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>Descartar cambios</button>
    </div>
  );
}
